import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  CssBaseline,
  Paper,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Cookies from "js-cookie";
import SharePointPage from "./SharePointPage";

// Import new components
import Header from "../components/Header/Header";
import ChatInput from "../components/Chat/ChatInput";
import ChatMessagesContainer from "../components/Chat/ChatMessagesContainer";
import FullScreenLoader from "../components/Common/FullScreenLoader";

// Import existing components
import DrawerMenu from "../components/DrawerMenu";
// import PresetQuestionsDialog from "../components/PresetQuestions";
import FilesList from "../components/FilesList";
import PasswordModal from "../components/PasswordModal";
import ChatContextManager from "../components/ChatContextManager";
import AdminDashboard from "../components/Admin/AdminDashboard";
import DocumentFiltersDrawer from "../components/DocumentFiltersDrawer";

// Import services and utilities
import {
  supervisorAgentResponse,
  cancelAgentRequest,
  submitChatFeedback,
  getCurrentUserEmail,
  getNameFromEmail,
  loadThreadChatList,
  getResearchModePreference,
  saveResearchModePreference,
  RESEARCH_MODE,
  CONVERSION_SOURCE,
  DEFAULT_DESIGN_INTELLIGENCE_SOURCES,
  CONSUMER_SENTIMENT_SOURCES,
} from "../services/apiService";
import { convertToCSV, downloadCSV } from "../utils/csvUtils";
import { v4 as uuidv4 } from "uuid";

// Import custom hook
import useChatState from "../hooks/useChatState";
import useChatTabs from "../hooks/useChatTabs";

const MainPage = ({ initialTab = "Concept Creator", contentView = null }) => {
  // Authentication state
  // When REACT_APP_SKIP_AUTH=true (local dev), bypass login entirely
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (process.env.REACT_APP_SKIP_AUTH === 'true') {
      const mockEmail = process.env.REACT_APP_MOCK_USER_EMAIL || 'test@campbells.com';
      const mockName = process.env.REACT_APP_MOCK_USER_NAME || 'Test User';
      const mockToken = btoa(`${mockEmail}:default2:${Date.now()}`);
      localStorage.setItem('authenticated', 'true');
      localStorage.setItem('accessToken', mockToken);
      localStorage.setItem('userId', 'local-dev-user');
      localStorage.setItem('userEmail', mockEmail);
      localStorage.setItem('userFullName', mockName);
      localStorage.setItem('persona', 'default2');
      return true;
    }
    return false;
  });
  const [, setUserEmail] = useState('');
  const [, setUserPersona] = useState('');
  const [, setUserName] = useState({ firstName: '', lastName: '' });
  const [isMicrosoftAuthLoading] = useState(false);

  const userRoleId = localStorage.getItem("userRoleId");

  // Menu items
  const menuItems = useMemo(() => {
    const items = [
      {
        id: 1,
        menuName: "Home",
        text: `Campbell's Harmonized Insights Platform`,
        icon: <AutoAwesomeIcon fontSize="small" sx={{ color: "#1B2559" }} />,
      },
      {
        id: 3,
        menuName: "CHIP Repository Hub",
        text: "CHIP Repository Hub",
        icon: <FolderSpecialIcon fontSize="small" sx={{ color: "#7b61ff" }} />,
      },
    ];

    if (userRoleId === "1") {
      items.push({
        id: 4,
        menuName: "Admin",
        text: "Admin",
        icon: <AdminPanelSettingsIcon fontSize="small" sx={{ color: "#d32f2f" }} />,
      });
    }

    return items;
  }, [userRoleId]);

  // Chat context state using custom hook
  const {
    activeTab,
    chatContexts,
    currentChatContextId,
    handleTabChange: hookHandleTabChange,
    handleCreateNewTab: hookHandleCreateNewTab,
    handleCreateChatContext: hookHandleCreateChatContext,
    handleUpdateChatContext,
    handleEditTab,
    handleDeleteChatContext: hookHandleDeleteChatContext,
    handleDeleteTab,
    handleCloseTab,
    handleSelectChatContext,
    truncateTabName,
  } = useChatTabs();

  // UI state
  const [showFilesList, setShowFilesList] = useState(false);
  const [showSharePointHub, setShowSharePointHub] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [selectedItem, setSelectedItem] = useState(menuItems[0]);
  const [open, setOpen] = useState(true);
  const [isDocumentFiltersDrawerExpanded, setIsDocumentFiltersDrawerExpanded] = useState(true);
  const [chatContextManagerOpen, setChatContextManagerOpen] = useState(false);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(false);
  const [chatHistoryCache, setChatHistoryCache] = useState(new Map());

  // Citation and data state
  const [citationopen, setCitationOpen] = useState(false);
  const [tables, setTables] = useState({});
  const [, setToolResults] = useState([]);

  // Datasource state
  const [selectDatasourse, onDisplayModeChange] = useState(DEFAULT_DESIGN_INTELLIGENCE_SOURCES);
  const [researchPreference, setResearchPreference] = useState({
    research_mode: RESEARCH_MODE.DESIGN_INTELLIGENCE,
    conversion_source: null,
    effective_datasources: DEFAULT_DESIGN_INTELLIGENCE_SOURCES,
  });
  const [filterDict, setFilterDict] = useState({});
  const [, onSelectedFileChange] = useState([]);
  const [selectedTabDetails, setSelectedTabDetails] = useState({});
  const chatTabsRef = useRef(null);
  const [userInfo, setUserInfo] = useState({
    accessToken: '',
    userId: '',
    email: '',
    full_name: '',
    persona: '',
  });

  useEffect(() => {
    // Listen for personaSet event to update user info
    const handlePersonaSet = (event) => {
      if (event.detail) {
        const userInfoData = {
          accessToken: event.detail.accessToken || '',
          userId: event.detail.userId || '',
          email: event.detail.email || '',
          full_name: event.detail.full_name || '',
          persona: event.detail.persona || '',
        };

        setUserInfo(userInfoData);

        // Also update localStorage to ensure persistence
        if (userInfoData.email) localStorage.setItem('userEmail', userInfoData.email);
        if (userInfoData.full_name) localStorage.setItem('userFullName', userInfoData.full_name);
        if (userInfoData.persona) localStorage.setItem('persona', userInfoData.persona);
      }
    };

    // Function to trigger logout
    const triggerLogout = () => {
      // Clear localStorage
      localStorage.removeItem('authenticated');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userFullName');
      localStorage.removeItem('persona');
      localStorage.removeItem('userRoleId');

      // Reset authentication state
      setIsAuthenticated(false);
      setUserInfo({
        accessToken: '',
        userId: '',
        email: '',
        full_name: '',
        persona: '',
      });
    };

    // Check for existing user data on component mount and dispatch event if available
    const checkExistingUserData = () => {
      const isAuth = localStorage.getItem('authenticated') === 'true';
      const accessToken = localStorage.getItem('accessToken');
      const userId = localStorage.getItem('userId');
      const email = localStorage.getItem('userEmail');
      const fullName = localStorage.getItem('userFullName');
      const persona = localStorage.getItem('persona');

      // Check if user is marked as authenticated but userId is missing
      if (isAuth && !userId) {
        triggerLogout();
        return;
      }

      if (isAuth && accessToken && userId && email && fullName) {
        const personaEvent = new CustomEvent('personaSet', {
          detail: {
            accessToken: accessToken,
            userId: userId,
            email: email,
            full_name: fullName,
            persona: persona || 'default'
          }
        });
        window.dispatchEvent(personaEvent);
      }
    };

    window.addEventListener('personaSet', handlePersonaSet);

    // Check for existing data on mount
    checkExistingUserData();

    return () => {
      window.removeEventListener('personaSet', handlePersonaSet);
    };
  }, []);

  // Load research preference when user authenticates
  useEffect(() => {
    if (isAuthenticated) {
      // Delay slightly to ensure all state is properly initialized
      const timer = setTimeout(() => {
        loadResearchPreference();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  // Chat state using custom hook
  const {
    messages,
    setMessages,
    input,
    setInput,
    isLoading,
    setIsLoading,
    latestUserInput,
    setLatestUserInput,
    setLatestBotResponseIndex,
    setSampleprompt,
    citationclickvisible,
    setCitationClickVisible,
    question_id,
  } = useChatState(selectDatasourse);

  const scrollBoxRef = useRef(null);
  const abortControllerRef = useRef(null);
  const previousSelectionKeyRef = useRef(null);

  // Convert snake_case to camelCase
  const toPascalCase = (str) =>
    str === "SPINS"
      ? str
      : str.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  const generateFilterString = useCallback((filters) => {
    return Object.entries(filters)
      .map(
        ([key, values]) => `<b>${toPascalCase(key)}</b>: ${values.join(", ")}`
      )
      .join("<br />");
  }, []);

  const researchModeDisplayName =
    researchPreference?.research_mode === RESEARCH_MODE.ALL_SOURCE
      ? "All Source"
      : researchPreference?.research_mode === RESEARCH_MODE.CONSUMER_SENTIMENT
      ? "Consumer Sentiment"
      : "Design Intelligence";

  const selectedSourcesLabel = toPascalCase(
    (Array.isArray(selectDatasourse) ? selectDatasourse : [selectDatasourse]).join(", ")
  );

  // Announce datasource only when datasource/filter selection actually changes.
  useEffect(() => {
    const selectionKey = JSON.stringify({
      selectDatasourse,
      filterDict,
    });

    // Baseline only; do not emit a notification on initial mount/hydration.
    if (previousSelectionKeyRef.current === null) {
      previousSelectionKeyRef.current = selectionKey;
      return;
    }

    if (previousSelectionKeyRef.current === selectionKey) {
      return;
    }

    previousSelectionKeyRef.current = selectionKey;

    const newSystemMessage = {
      text:
        `Research Mode - <b>${researchModeDisplayName}</b><br />` +
        `Sources - <b>` +
        selectedSourcesLabel +
        `</b><br />` +
        (selectDatasourse.includes("documents") && Object.keys(filterDict).length > 0 ? '<b>Document Filter:</b><br />' + generateFilterString(filterDict) : ""),
      isUser: false,
      isLoading: false,
      responseTime: 0,
      selectDatasourse: selectDatasourse,
      isSystemNotification: true,
      additional_agent_responses: {},
      reformatted_question: "",
    };

    setMessages((prevMessages) => {
      if (!prevMessages || prevMessages.length === 0) {
        return prevMessages;
      }

      const lastMessage = prevMessages[prevMessages.length - 1];
      if (lastMessage?.isSystemNotification) {
        return [...prevMessages.slice(0, -1), newSystemMessage];
      }
      return [...prevMessages, newSystemMessage];
    });
  }, [filterDict, generateFilterString, researchModeDisplayName, selectDatasourse, selectedSourcesLabel, setMessages]);

  // Get user info when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const email = getCurrentUserEmail();
      const persona = localStorage.getItem('persona');
      const nameInfo = getNameFromEmail(email);

      setUserEmail(email || 'user@campbell.com');
      setUserPersona(persona || 'default');
      setUserName(nameInfo);
    }
  }, [isAuthenticated]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const getEffectiveSourcesForPreference = useCallback((preference) => {
    const baseSources = Array.isArray(preference?.effective_datasources)
      ? preference.effective_datasources
      : DEFAULT_DESIGN_INTELLIGENCE_SOURCES;

    if (preference?.research_mode !== RESEARCH_MODE.ALL_SOURCE) {
      return baseSources;
    }

    const conversionSource = preference?.conversion_source || CONVERSION_SOURCE.CIRCANA;
    const excludedSource =
      conversionSource === CONVERSION_SOURCE.SPINS ? "circana" : "spins";

    return baseSources.filter(
      (source) => String(source || "").toLowerCase() !== excludedSource
    );
  }, []);

  const applyPreferenceToDatasource = useCallback((preference) => {
    const effectiveSources = getEffectiveSourcesForPreference(preference);
    onDisplayModeChange(effectiveSources);
    setResearchPreference({
      research_mode: preference?.research_mode || RESEARCH_MODE.DESIGN_INTELLIGENCE,
      conversion_source: preference?.conversion_source || null,
      effective_datasources: effectiveSources,
    });
  }, [getEffectiveSourcesForPreference]);

  const loadResearchPreference = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    const response = await getResearchModePreference();
    if (response?.success && response?.preference) {
      applyPreferenceToDatasource(response.preference);
      return;
    }

    applyPreferenceToDatasource({
      research_mode: RESEARCH_MODE.DESIGN_INTELLIGENCE,
      conversion_source: null,
      effective_datasources: DEFAULT_DESIGN_INTELLIGENCE_SOURCES,
    });
  }, [applyPreferenceToDatasource, isAuthenticated]);

  const handleSaveResearchPreference = useCallback(async (payload) => {
    const normalizedPayload = {
      research_mode: payload?.research_mode || RESEARCH_MODE.DESIGN_INTELLIGENCE,
      conversion_source:
        payload?.research_mode === RESEARCH_MODE.ALL_SOURCE
          ? payload?.conversion_source || CONVERSION_SOURCE.CIRCANA
          : null,
    };

    const response = await saveResearchModePreference(normalizedPayload);
    if (response?.success && response?.preference) {
      applyPreferenceToDatasource(response.preference);
    }
    return response;
  }, [applyPreferenceToDatasource]);

  const handleItemClick = useCallback((item) => {
    // console.log("Menu item clicked:", item, chatTabsRef.current, userInfo.userId, userInfo.accessToken);
    if (item.text === `Campbell's Harmonized Insights Platform`) {
      setSelectedItem(item);
      setShowFilesList(false);
      setShowSharePointHub(false);
      setShowAdmin(false);

      // Call fetchThreads immediately when navigating to home page
      if (chatTabsRef.current && userInfo.userId && userInfo.accessToken) {
        console.log("Calling fetchThreads immediately on home navigation");
        chatTabsRef.current.fetchThreads(true); // Skip loader since it's a refresh
      }
    }
    // else if (item.text === "Preset Questions") {
    //   setIsDialogOpen(true);
    // } 
    else if (item.text === "List of Uploaded Files") {
      setSelectedItem(item);
      setShowFilesList(true);
      setShowSharePointHub(false);
    } else if (item.text === "CHIP Repository Hub") {
      setSelectedItem(item);
      setShowSharePointHub(true);
      setShowFilesList(false);
      setShowAdmin(false);
    } else if (item.text === "Admin") {
      setSelectedItem(item);
      setShowAdmin(true);
      setShowSharePointHub(false);
      setShowFilesList(false);
    }
  }, [userInfo.accessToken, userInfo.userId]);

  useEffect(() => {
    const matchingItem = menuItems.find((item) => item.text === initialTab);
    if (matchingItem) {
      handleItemClick(matchingItem);
    }
  }, [handleItemClick, initialTab, menuItems]);

  const handleFileSelect = (fileName) => {
    if (fileName) {
      onSelectedFileChange([fileName]);
      setShowFilesList(false);
      setSelectedItem(menuItems[0]);
      onDisplayModeChange("fileDatasource");
    }
  };

  const handleFileDelete = async (fileName) => {
    alert(`File "${fileName}" would be deleted (API not connected)`);
  };

  // Add a function to clear chat and start new conversation
  const handleStartNewChat = async () => {
    try {
      // Generate new session IDs
      const newFollowsessionId = uuidv4();
      const newEnrichsessionId = uuidv4();
      const newPlanGenerationId = uuidv4();
      const new_question_id = uuidv4();

      // Store new session IDs
      localStorage.setItem("followsession_id", newFollowsessionId);
      localStorage.setItem("enrichsession_id", newEnrichsessionId);
      localStorage.setItem("plan_generation_id", newPlanGenerationId);
      localStorage.setItem("question_id", new_question_id);

      const userFullName = localStorage.getItem("userFullName") || "User";

      // Clear chat except for the first welcome message
      const welcomeMessage = {
        text: `Hello ${userFullName}! I'm your <b>AI Assistant Chatbot</b>. How can I help you today?`,
        isUser: false,
        isLoading: false,
        responseTime: 0,
        selectDatasourse: selectDatasourse,
        isSystemNotification: false,
      };

      const systemDatasourceMessage = {
        text:
          `Research Mode - <b>${researchModeDisplayName}</b><br />` +
          `Sources - <b>` +
          selectedSourcesLabel +
          `</b><br />` +
          (selectDatasourse.includes("documents") && Object.keys(filterDict).length > 0 ? '<b>Document Filter:</b><br />' + generateFilterString(filterDict) : ""),
        isUser: false,
        isLoading: false,
        responseTime: 0,
        selectDatasourse: selectDatasourse,
        isSystemNotification: true,
      };

      setMessages([welcomeMessage, systemDatasourceMessage]);
      setSampleprompt(true);
      setCitationClickVisible(false);

      // Clear cookie
      Cookies.remove("chatMessages", { path: "/" });
    } catch (error) {
      console.error("Failed to clear chat context:", error);
    }
  };

  // Function to sanitize user input
  const sanitizeUserInput = (input) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = input;

    const blacklistedTags = ["script", "iframe", "object", "embed"];
    blacklistedTags.forEach((tag) => {
      const elements = tempDiv.getElementsByTagName(tag);
      while (elements[0]) elements[0].parentNode.removeChild(elements[0]);
    });

    const allElements = tempDiv.getElementsByTagName("*");
    for (let i = 0; i < allElements.length; i++) {
      const el = allElements[i];
      [...el.attributes].forEach((attr) => {
        if (
          attr.name.startsWith("on") ||
          (attr.value && /^\s*javascript:/i.test(attr.value.toString()))
        ) {
          el.removeAttribute(attr.name);
        }
      });
    }

    return tempDiv.textContent || tempDiv.innerText || "";
  };
  const generateSessionId = () => uuidv4();

  // Function to update cache when new messages are added to existing threads
  const updateChatHistoryCache = (threadId, newMessages) => {
    if (threadId && threadId !== 'default') {
      setChatHistoryCache(prev => new Map(prev.set(threadId, newMessages)));
    }
  };

  const applyFeedbackToMessages = (currentMessages, questionId, feedback) => {
    return currentMessages.map((message) => {
      if (message?.question_id !== questionId || message.isUser || message.isSystemNotification) {
        return message;
      }

      return {
        ...message,
        feedback_type: feedback?.feedback_type || null,
        feedback_reason: feedback?.feedback_reason || "",
        feedback_comment: feedback?.feedback_comment || "",
      };
    });
  };

  const handleSubmitFeedback = async ({ questionId, threadId, feedbackType, feedbackReason, feedbackComment }) => {
    const response = await submitChatFeedback(
      questionId,
      threadId,
      userInfo.userId,
      feedbackType,
      feedbackReason,
      feedbackComment,
    );

    if (!response || response.error || response.status === 'error') {
      return {
        success: false,
        message: response?.message || 'Unable to save feedback.',
      };
    }

    const savedFeedback = response.feedback || {
      feedback_type: feedbackType,
      feedback_reason: feedbackReason || null,
      feedback_comment: feedbackComment || null,
    };

    setMessages((prevMessages) => {
      const updated = applyFeedbackToMessages(prevMessages, questionId, savedFeedback);
      const feedbackMessageIndex = updated.findIndex((message) => message?.question_id === questionId && !message.isUser);

      if (feedbackMessageIndex >= 0) {
        updateCookieWithNewMessages(updated[feedbackMessageIndex], feedbackMessageIndex);
      }

      updateChatHistoryCache(threadId || activeTab, updated);
      return updated;
    });

    return { success: true, feedback: savedFeedback };
  };

  // Helper function to determine tab type and set tab details
  const determineTabType = () => {
    if (!activeTab) {
      console.log('Chat Context Group Type: No active tab');
      return 'new_thread';
    }

    if (activeTab === 'default') {
      setSelectedTabDetails({ id: generateSessionId(), name: 'Default', type: 'new_thread' });
      return 'new_thread';
    }

    // Check if it's an existing API thread
    const isApiThread = chatContexts.find(ctx => ctx.id === activeTab);
    if (isApiThread) {
      setSelectedTabDetails({ id: activeTab, name: isApiThread.name, type: 'new_thread' });
      return 'new_thread';
    }

    // It's a saved thread
    setSelectedTabDetails({ id: activeTab, name: "Existing", type: 'existing_thread' });
    return 'existing_thread';
  };

  // Helper function to create and process user message
  const processUserMessage = (sanitizedInput) => {
    const newMessage = {
      text: sanitizedInput,
      isUser: true,
      citationclickvisible: false,
      isSystemNotification: false,
    };

    updateCookieWithNewMessages(newMessage);
    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    setLatestUserInput(sanitizedInput);
    setInput("");

    const loadingMessage = { text: "", isUser: false, isLoading: true };
    setMessages((prevMessages) => [...prevMessages, loadingMessage]);

    return updatedMessages;
  };

  // Helper function to format bot response text
  const formatBotResponseText = (response) => {
    return response
      .replace(/^\s*### (.*)$/gm, "<h1>$1</h1>")
      .replace(/^\s*## (.*)$/gm, "<h1>$1</h1>")
      .replace(/^\s*# (.*)$/gm, "<h1>$1</h1>")
      .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
      .replace(/^---\s*$/gm, "")
      .replace(/\n/g, "<br>");
  };

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const linkifyAbbreviationCandidates = (formattedText, abbreviationOptions = []) => {
    if (!formattedText || !Array.isArray(abbreviationOptions) || abbreviationOptions.length === 0) {
      return formattedText;
    }

    const candidateSet = new Map();

    abbreviationOptions.forEach((option) => {
      if (!option || !Array.isArray(option.candidates)) return;
      option.candidates.forEach((candidate) => {
        if (!candidate || typeof candidate !== "string") return;
        const normalized = candidate.trim();
        if (!normalized) return;
        const key = normalized.toLowerCase();
        if (!candidateSet.has(key)) {
          candidateSet.set(key, normalized);
        }
      });
    });

    const candidates = Array.from(candidateSet.values()).sort((a, b) => b.length - a.length);
    if (candidates.length === 0) {
      return formattedText;
    }

    const pattern = candidates.map((candidate) => escapeRegExp(candidate)).join("|");
    const regex = new RegExp(`\\b(${pattern})\\b`, "gi");

    const container = document.createElement("div");
    container.innerHTML = formattedText;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!node || !node.nodeValue || !node.parentElement) continue;
      if (node.parentElement.closest("a")) continue;
      textNodes.push(node);
    }

    textNodes.forEach((textNode) => {
      const originalText = textNode.nodeValue;
      if (!originalText) return;

      regex.lastIndex = 0;
      if (!regex.test(originalText)) {
        return;
      }

      regex.lastIndex = 0;
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(originalText)) !== null) {
        const matchedText = match[0];
        const matchIndex = match.index;

        if (matchIndex > lastIndex) {
          fragment.appendChild(document.createTextNode(originalText.slice(lastIndex, matchIndex)));
        }

        const link = document.createElement("a");
        link.textContent = matchedText;
        link.setAttribute("href", "#");
        link.setAttribute("data-abbreviation-candidate", matchedText);
        link.style.textDecoration = "underline";
        link.style.fontWeight = "600";
        link.style.cursor = "pointer";
        fragment.appendChild(link);

        lastIndex = matchIndex + matchedText.length;
      }

      if (lastIndex < originalText.length) {
        fragment.appendChild(document.createTextNode(originalText.slice(lastIndex)));
      }

      textNode.parentNode.replaceChild(fragment, textNode);
    });

    return container.innerHTML;
  };

  const handleAbbreviationCandidateClick = (candidateText) => {
    if (!candidateText || typeof candidateText !== "string") return;

    setInput((prevInput) => {
      const trimmedInput = (prevInput || "").trim();
      if (!trimmedInput) return candidateText;
      const needsSpace = /\s$/.test(prevInput || "") ? "" : " ";
      return `${prevInput}${needsSpace}${candidateText}`;
    });

    const inputElement = document.getElementById("inputtext");
    if (inputElement) {
      inputElement.focus();
    }
  };

  // Helper function to handle successful bot response
  const handleBotResponseSuccess = (botResponse, updatedMessages, tabType) => {
    // const abbreviationOptions = 
    // [
    //     {
    //         "candidates": [
    //             "Gluten-Free",
    //             "Goldfish"
    //         ],
    //         "term": "GF"
    //     },
    //     {
    //         "candidates": [
    //             "PEPPERIDGE FARM PLANTS",
    //             "Pepperidge Farm",
    //             "Pepperidge Farm the brand"
    //         ],
    //         "term": "PF"
    //     }
    // ];

    const abbreviationOptions = Array.isArray(botResponse?.abbreviation_options)
      ? botResponse.abbreviation_options
      : [];
    const formattedText = linkifyAbbreviationCandidates(
      formatBotResponseText(botResponse.response),
      abbreviationOptions
    );
    let finalMessages = updatedMessages;

    setMessages((prevMessages) => {
      const newUpdatedMessages = [...prevMessages];



      newUpdatedMessages[newUpdatedMessages.length - 1] = {
        text: formattedText,
        isUser: false,
        isLoading: false,
        citations: botResponse?.source_data,
        responseTime: botResponse?.response_time,
        selectDatasourse: selectDatasourse,
        isSystemNotification: false,
        additional_agent_responses: botResponse?.additional_agent_responses || {},
        reformatted_question: botResponse?.reformatted_question || "",
        abbreviation_options: abbreviationOptions || [],
        question_id: botResponse?.question_id || null,
        thread_id: botResponse?.thread_id || activeTab,
        feedback_type: null,
        feedback_reason: "",
        feedback_comment: "",
      };

      setToolResults(botResponse.source_data);
      updateCookieWithNewMessages(newUpdatedMessages[newUpdatedMessages.length - 1]);
      updateChatHistoryCache(activeTab, newUpdatedMessages);
      finalMessages = newUpdatedMessages;

      return newUpdatedMessages;
    });

    return handlePostResponseThreadManagement(tabType, botResponse, finalMessages);
  };

  // Helper function to handle thread management after response
  const handlePostResponseThreadManagement = async (tabType, botResponse, updatedMessages) => {
    if (tabType === 'new_thread' && chatTabsRef.current) {
      const currentActiveTab = activeTab;

      if (botResponse?.thread_id) {
        updateChatHistoryCache(botResponse.thread_id, updatedMessages);
      }

      // Call fetchThreads with skipLoader=true
      await chatTabsRef.current.fetchThreads(true);

      // Delete the local context only if it's not the default tab
      if (currentActiveTab !== 'default') {
        hookHandleDeleteChatContext(currentActiveTab);
      }

      setTimeout(() => {
        if (botResponse?.thread_id) {
          console.log("Switching to new thread:", botResponse.thread_id);
          hookHandleTabChange(null, botResponse.thread_id);
        } else if (currentActiveTab && currentActiveTab !== 'default') {
          hookHandleTabChange(null, currentActiveTab);
        }
      }, 500);
    }
  };

  // Helper function to cancel ongoing request — fires immediately on button click
  const handleCancelRequest = () => {
    // 1. Immediately show the cancelled message and re-enable input
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      if (updatedMessages.length > 0) {
        updatedMessages[updatedMessages.length - 1] = {
          text: "You have cancelled request.",
          isUser: false,
          isLoading: false,
          citations: {},
          responseTime: 0,
          selectDatasourse: selectDatasourse,
          isSystemNotification: false,
          additional_agent_responses: {},
          reformatted_question: "",
          abbreviation_options: [],
        };
      }
      return updatedMessages;
    });
    setIsLoading(false);

    // 2. Abort the in-flight fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // 3. Notify server (fire-and-forget)
    cancelAgentRequest(question_id).catch(() => { });
  };

  // Helper function to make API call to supervisor agent
  const callSupervisorAgent = async (sanitizedInput, tabType) => {
    const persona = localStorage.getItem("persona");
    const lowerCaseSelectDatasourse = Array.isArray(selectDatasourse)
      ? selectDatasourse.map((v) => v.toLowerCase())
      : [];
    const conversionSourcePayload =
      researchPreference?.research_mode === RESEARCH_MODE.ALL_SOURCE
        ? (researchPreference?.conversion_source || CONVERSION_SOURCE.CIRCANA)
        : "";
    const user_question_id = uuidv4();
    localStorage.setItem("question_id", user_question_id);

    abortControllerRef.current = new AbortController();

    return await supervisorAgentResponse(
      sanitizedInput,
      user_question_id,
      lowerCaseSelectDatasourse,
      filterDict,
      persona,
      userInfo.userId,
      activeTab === 'default' ? 'default_' + generateSessionId() : activeTab,
      tabType,
      conversionSourcePayload,
      abortControllerRef.current.signal
    );
  };

  const handleSendMessage = async () => {
    const sanitizedInput = sanitizeUserInput(input.trim());

    if (sanitizedInput === "") {
      return;
    }

    setSelectedTabDetails({});
    setSampleprompt(false);
    setIsLoading(true);
    setCitationClickVisible(true);

    const tabType = determineTabType();
    const updatedMessages = processUserMessage(sanitizedInput);
    const newBotResponseIndex = messages.length + 1;

    try {
      const botResponse = await callSupervisorAgent(sanitizedInput, tabType);

      if (botResponse != null) {
        await handleBotResponseSuccess(botResponse, updatedMessages, tabType);
      }

      setLatestBotResponseIndex(newBotResponseIndex);
    } catch (error) {
      if (error.name !== "AbortError") {
        // AbortError is already handled by handleCancelRequest — only show error for real failures
        console.log(error.message);
        // handleBotResponseError();
      }
      setLatestBotResponseIndex(newBotResponseIndex);
    }

    setIsLoading(false);
    abortControllerRef.current = null;
  };

  // Helper function to find the last user message
  const findLastUserMessageIndex = () => {
    const lastUserMessageIndex = [...messages]
      .reverse()
      .findIndex((msg) => msg.isUser && !msg.isSystemNotification);

    if (lastUserMessageIndex === -1) return null;

    const actualIndex = messages.length - 1 - lastUserMessageIndex;
    const botResponseIndex = actualIndex + 1;

    if (botResponseIndex >= messages.length) return null;

    return {
      userMessageIndex: actualIndex,
      botResponseIndex,
      userMessage: messages[actualIndex]
    };
  };

  // Helper function to set loading state for regeneration
  const setRegenerateLoading = (botResponseIndex) => {
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      updatedMessages[botResponseIndex] = {
        ...updatedMessages[botResponseIndex],
        text: "",
        isUser: false,
        isLoading: true,
        isSystemNotification: false,
      };
      return updatedMessages;
    });
  };

  // Helper function to call supervisor agent for regeneration
  const callRegenerateAgent = async (sanitizedUserInput) => {
    abortControllerRef.current = new AbortController();
    const lowerCaseSelectDatasourse = Array.isArray(selectDatasourse)
      ? selectDatasourse.map((v) => v.toLowerCase())
      : [];
    const conversionSourcePayload =
      researchPreference?.research_mode === RESEARCH_MODE.ALL_SOURCE
        ? (researchPreference?.conversion_source || CONVERSION_SOURCE.CIRCANA)
        : "";

    return await supervisorAgentResponse(
      sanitizedUserInput,
      question_id,
      lowerCaseSelectDatasourse,
      filterDict,
      localStorage.getItem("persona"),
      userInfo.userId,
      "",
      "",
      conversionSourcePayload,
      abortControllerRef.current.signal
    );
  };

  // Helper function to format regenerated response text
  const formatRegeneratedResponseText = (responseText) => {
    return responseText
      .replace(/^\s*### (.*)$/gm, "<h1>$1</h1>")
      .replace(/^\s*## (.*)$/gm, "<h1>$1</h1>")
      .replace(/^\s*# (.*)$/gm, "<h1>$1</h1>")
      .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
      .replace(/^---\s*$/gm, "")
      .replace(/\n/g, "<br>");
  };

  // Helper function to handle successful regeneration
  const handleRegenerateSuccess = (botResponse, botResponseIndex) => {
    const abbreviationOptions = Array.isArray(botResponse?.abbreviation_options)
      ? botResponse.abbreviation_options
      : [];
    const formattedText = linkifyAbbreviationCandidates(
      formatRegeneratedResponseText(botResponse.response),
      abbreviationOptions
    );

    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const existingMessage = updatedMessages[botResponseIndex] || {};
      updatedMessages[botResponseIndex] = {
        ...existingMessage,
        text: formattedText,
        isUser: false,
        isLoading: false,
        citations: botResponse?.source_data,
        responseTime: botResponse?.response_time,
        selectDatasourse: selectDatasourse,
        isSystemNotification: false,
        isRegenerated: true,
        additional_agent_responses: botResponse?.additional_agent_responses || {},
        reformatted_question: botResponse?.reformatted_question || "",
        abbreviation_options: abbreviationOptions,
        question_id: existingMessage.question_id || botResponse?.question_id || question_id,
        thread_id: existingMessage.thread_id || botResponse?.thread_id || activeTab,
      };

      updateCookieWithNewMessages(updatedMessages[botResponseIndex], botResponseIndex);
      return updatedMessages;
    });

    setToolResults(botResponse.source_data || []);
  };

  // Helper function to handle regeneration error
  const handleRegenerateError = (error, botResponseIndex) => {
    console.error("Error regenerating response:", error);

    const formattedText = "Oops! Something went wrong during regeneration. Please try again.<br/>";

    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const existingMessage = updatedMessages[botResponseIndex] || {};
      updatedMessages[botResponseIndex] = {
        ...existingMessage,
        text: formattedText,
        isUser: false,
        isLoading: false,
        citations: {},
        responseTime: 0,
        selectDatasourse: selectDatasourse,
        isSystemNotification: false,
        additional_agent_responses: {},
        reformatted_question: "",
      };

      updateCookieWithNewMessages(updatedMessages[botResponseIndex], botResponseIndex);
      return updatedMessages;
    });
  };

  // Helper function to handle post-regeneration scrolling
  const scrollToBottomAfterRegenerate = () => {
    setTimeout(() => {
      if (scrollBoxRef.current) {
        scrollBoxRef.current.scrollTo({
          top: scrollBoxRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  // Function to regenerate the last AI response
  const handleRegenerateResponse = async () => {
    if (!latestUserInput || messages.length === 0) {
      return;
    }

    setIsLoading(true);

    const messageIndexes = findLastUserMessageIndex();
    if (!messageIndexes) {
      setIsLoading(false);
      return;
    }

    const { userMessage, botResponseIndex } = messageIndexes;
    const sanitizedUserInput = sanitizeUserInput(userMessage.text);

    setRegenerateLoading(botResponseIndex);

    try {
      const botResponse = await callRegenerateAgent(sanitizedUserInput);

      if (botResponse) {
        handleRegenerateSuccess(botResponse, botResponseIndex);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        // AbortError already handled by handleCancelRequest
        handleRegenerateError(error, botResponseIndex);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      scrollToBottomAfterRegenerate();
    }
  };

  const handleCitationClickOpen = (citations) => {
    if (citations && typeof citations === "string") {
      setTables({ paragraph: citations });
    } else if (citations && typeof citations === "object") {
      let formattedCitations = {};
      Object.keys(citations).forEach((tableName) => {
        const tableData = citations[tableName];
        if (Array.isArray(tableData) && tableData.length > 0) {
          formattedCitations[tableName] = tableData;
        }
      });
      setTables(formattedCitations);
    } else {
      setTables({ Error: [{ message: "No citation data available." }] });
    }

    setCitationOpen(true);
  };

  const handleCitationClose = () => {
    setCitationOpen(false);
  };

  const handleDownloadAll = () => {
    Object.entries(tables).forEach(([tableName, tableData], index) => {
      setTimeout(() => {
        const csv = convertToCSV(tableData);
        downloadCSV(csv, `${tableName}.csv`);
      }, index * 500);
    });
  };

  const updateCookieWithNewMessages = (newMessage, index = null) => {
    const serializedExistingMessages = Cookies.get("chatMessages");
    const existingMessages = serializedExistingMessages
      ? JSON.parse(serializedExistingMessages)
      : [];

    if (index !== null && existingMessages[index]) {
      existingMessages[index] = { ...existingMessages[index], ...newMessage };
    } else {
      const { citations, ...messageWithoutCitations } = newMessage;
      existingMessages.push(messageWithoutCitations);
    }
    const serializedUpdatedMessages = JSON.stringify(existingMessages);
    Cookies.set("chatMessages", serializedUpdatedMessages, {
      path: "/",
      expires: 365,
    });
  };

  useLayoutEffect(() => {
    if (scrollBoxRef.current) {
      const scrollHeight = scrollBoxRef.current.scrollHeight;
      const clientHeight = scrollBoxRef.current.clientHeight;
      if (scrollHeight > clientHeight) {
        scrollBoxRef.current.scrollTop = scrollHeight;
      }
    }
  }, [messages]);

  // Modified context creation handler
  const onCreateChatContext = (newContext) => {
    const userFullName = localStorage.getItem("userFullName") || "User";
    const welcomeMessage = {
      text: `Hello ${userFullName}! I'm your <b>AI Assistant Chatbot</b>. How can I help you today?`,
      isUser: false,
      isLoading: false,
      responseTime: 0,
      selectDatasourse: selectDatasourse,
      isSystemNotification: false,
    };
    const systemDatasourceMessage = {
      text: `Research Mode - <b>${researchModeDisplayName}</b><br />Sources - <b>${selectedSourcesLabel}</b>`,
      isUser: false,
      isLoading: false,
      responseTime: 0,
      selectDatasourse: selectDatasourse,
      isSystemNotification: true,
      additional_agent_responses: {},
      reformatted_question: "",
    };
    setMessages([welcomeMessage, systemDatasourceMessage]);
  };

  // Add state for default tab name
  const [defaultTabName, setDefaultTabName] = useState("Default Chat");

  // Handle editing default tab name
  const handleEditDefaultTab = (newName) => {
    setDefaultTabName(newName);
  };

  // Handle creating new tab directly
  const handleCreateNewTab = () => {
    hookHandleCreateNewTab(onCreateChatContext);
  };

  // Enhanced context creation handler (from context manager)
  const handleCreateChatContext = (newContext) => {
    hookHandleCreateChatContext(newContext, onCreateChatContext);
  };

  // Enhanced context deletion handler
  const handleDeleteChatContext = (contextId) => {
    hookHandleDeleteChatContext(contextId);
  };

  // Helper function to create standard welcome messages
  const createWelcomeMessages = useCallback(() => {
    const userFullName = localStorage.getItem("userFullName") || "User";
    const welcomeMessage = {
      text: `Hello ${userFullName}! I'm your <b>AI Assistant Chatbot</b>. How can I help you today?`,
      isUser: false,
      isLoading: false,
      responseTime: 0,
      selectDatasourse: selectDatasourse,
      isSystemNotification: false,
    };

    const systemDatasourceMessage = {
      text: `Research Mode - <b>${researchModeDisplayName}</b><br />Sources - <b>${selectedSourcesLabel}</b>`,
      isUser: false,
      isLoading: false,
      responseTime: 0,
      selectDatasourse: selectDatasourse,
      isSystemNotification: true,
      additional_agent_responses: {},
      reformatted_question: "",
    };

    return [welcomeMessage, systemDatasourceMessage];
  }, [researchModeDisplayName, selectDatasourse, selectedSourcesLabel]);

  // Helper function to safely parse JSON fields
  const parseJsonField = useCallback((fieldValue, fieldName) => {
    if (!fieldValue) return {};

    try {
      if (typeof fieldValue === 'string') {
        let cleanedData = fieldValue.trim();
        if (cleanedData.startsWith("'") && cleanedData.endsWith("'")) {
          cleanedData = cleanedData.slice(1, -1);
        }
        return JSON.parse(cleanedData);
      } else {
        return fieldValue;
      }
    } catch (error) {
      console.warn(`Failed to parse ${fieldName} JSON:`, error);
      console.warn(`Original ${fieldName}:`, fieldValue);
      return {};
    }
  }, []);

  // Helper function to format chat answer text
  const formatChatAnswer = useCallback((answer) => {
    return answer
      .replace(/^\s*### (.*)$/gm, "<h1>$1</h1>")
      .replace(/^\s*## (.*)$/gm, "<h1>$1</h1>")
      .replace(/^\s*# (.*)$/gm, "<h1>$1</h1>")
      .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
      .replace(/^---\s*$/gm, "")
      .replace(/\n/g, "<br>");
  }, []);

  // Helper function to process datasource
  const processDatasource = useCallback((datasource) => {
    if (!datasource) return selectDatasourse;
    return typeof datasource === 'string'
      ? datasource.split(',').map(item => item.trim())
      : [datasource];
  }, [selectDatasourse]);

  // Load chat history when switching tabs
  const loadChatHistory = useCallback(async (threadId) => {
    if (!threadId || !userInfo.userId) {
      console.log("Check1 - No threadId or userInfo");
      setMessages(createWelcomeMessages());
      return;
    }

    // For default tab, only reset if no user messages exist
    if (threadId === 'default') {
      setMessages((prevMessages) => {
        const hasUserMessages = prevMessages.some(
          (msg) => msg.isUser && !msg.isSystemNotification
        );
        return hasUserMessages ? prevMessages : createWelcomeMessages();
      });
      return;
    }

    // Check if it's a local chat context (not a saved thread)
    const isLocalContext = chatContexts.find(ctx => ctx.id === threadId);
    if (isLocalContext) {
      console.log("Check3 - Local context");
      setMessages(createWelcomeMessages());
      return;
    }

    // Check if chat history is already cached for this thread
    if (chatHistoryCache.has(threadId)) {
      // console.log("Check4 - Loading from cache for thread:", threadId);
      const cachedMessages = chatHistoryCache.get(threadId);
      setMessages(cachedMessages);
      return;
    }

    // If not cached, load from API
    // console.log("Loading from API for thread:", threadId);

    try {
      // Show loading state while fetching chat history
      setIsChatHistoryLoading(true);

      const response = await loadThreadChatList(userInfo.userId, threadId);
      await loadResearchPreference();

      let allMessages = [];

      if (response && !response.error && response.chats && response.chats.length > 0) {
        const chatHistory = [];

        response.chats.forEach(chat => {
          // Add user message
          if (chat.question) {
            chatHistory.push({
              text: chat.question,
              isUser: true,
              isLoading: false,
              responseTime: 0,
              selectDatasourse: processDatasource(chat.datasource),
              isSystemNotification: false,
              citations: {},
              additional_agent_responses: {},
              reformatted_question: "",
              timestamp: chat.timestamp,
              ui_filters: {},
              chatType: "chatQuestion",
              question_id: chat.question_id,
              thread_id: chat.thread_id,
            });
          }

          // Add bot response
          if (chat.answer) {
            const chatAnswer = formatChatAnswer(chat.answer);
            const citations = parseJsonField(chat.source_data, 'source_data');
            const additionalAgentResponses = parseJsonField(chat.additional_agent_responses, 'additional_agent_responses');
            const ui_filters = parseJsonField(chat.ui_filters, 'ui_filters');

            chatHistory.push({
              text: chatAnswer,
              isUser: false,
              isLoading: false,
              responseTime: chat.response_time ? (parseFloat(chat.response_time) || 0) : 0,
              selectDatasourse: processDatasource(chat.datasource),
              isSystemNotification: false,
              citations: citations,
              additional_agent_responses: additionalAgentResponses,
              reformatted_question: chat.question_rephrased || "",
              timestamp: chat.timestamp,
              ui_filters: ui_filters,
              chatType: "chatAnswer",
              question_id: chat.question_id,
              thread_id: chat.thread_id,
              feedback_type: chat.feedback_type || null,
              feedback_reason: chat.feedback_reason || "",
              feedback_comment: chat.feedback_comment || "",
            });
          }
        });

        // Add welcome messages at the beginning
        allMessages = [...createWelcomeMessages(), ...chatHistory];
        // console.log("Loaded chat history for thread:", threadId, allMessages);
      } else {
        // Show welcome messages for threads with no history
        allMessages = createWelcomeMessages();
      }

      // Cache the messages for this thread
      setChatHistoryCache(prev => new Map(prev.set(threadId, allMessages)));
      setMessages(allMessages);

    } catch (error) {
      console.error("Error loading chat history:", error);
      // Fallback to welcome messages on error
      setMessages(createWelcomeMessages());
    } finally {
      // Hide loading state after chat history is loaded or failed
      setIsChatHistoryLoading(false);
    }

  }, [chatContexts, chatHistoryCache, createWelcomeMessages, formatChatAnswer, loadResearchPreference, parseJsonField, processDatasource, setMessages, userInfo.userId]);

  // Enhanced tab change handler
  const handleTabChange = (event, newValue) => {
    // console.log("Tab change triggered:", { event, newValue, userInfo: userInfo.userId });
    // Load chat history for the selected tab first
    hookHandleTabChange(event, newValue, () => setChatContextManagerOpen(true));
    // Load chat history after tab change - ensure this happens for auto-selected tabs too
    if (newValue && userInfo.userId) {
      // console.log("Loading chat history for tab:", newValue);
      // Use setTimeout to ensure the tab change is processed before loading history
      setTimeout(() => {
        loadChatHistory(newValue);
      }, 100);
    }
  };

  // Watch for activeTab changes when user is logged in to ensure chat history loads
  useEffect(() => {
    if (activeTab && userInfo.userId && activeTab === 'default') {
      // console.log("Active tab changed, loading chat history:", activeTab);
      // Small delay to ensure tab is properly set
      setTimeout(() => {
        loadChatHistory(activeTab);
      }, 200);
    }
  }, [activeTab, loadChatHistory, userInfo.userId]);

  const isDocumentFiltersDrawerOpen =
    open &&
    selectedItem.id === 1 &&
    (selectDatasourse.includes("documents") ||
      researchPreference?.research_mode === RESEARCH_MODE.ALL_SOURCE);

  const rightPanelOffset = isDocumentFiltersDrawerOpen
    ? (isDocumentFiltersDrawerExpanded ? 325 : 60)
    : 0;

  return (
    <>
      <CssBaseline />

      {/* Full-screen loader for chat history loading */}
      <FullScreenLoader
        loading={isChatHistoryLoading}
        message="Loading..."
        size={40}
      />

      {/* Header Component */}
      <Header
        selectedItem={selectedItem}
        open={open}
        isAuthenticated={isAuthenticated}
        researchPreference={researchPreference}
        onSaveResearchPreference={handleSaveResearchPreference}
      />

      {/* Main Container */}
      <Grid container sx={{ position: "relative", zIndex: 1 }}>
        <DrawerMenu
          open={open}
          menuItems={menuItems}
          selectedItem={selectedItem}
          handleItemClick={handleItemClick}
          handleDrawerToggle={handleDrawerToggle}
          chatTabsRef={chatTabsRef}
          activeTab={activeTab}
          chatContexts={chatContexts}
          onTabChange={handleTabChange}
          onCloseTab={handleCloseTab}
          onCreateNewTab={handleCreateNewTab}
          onEditTab={handleEditTab}
          onDeleteTab={handleDeleteTab}
          onEditDefaultTab={handleEditDefaultTab}
          defaultTabName={defaultTabName}
          truncateTabName={truncateTabName}
          selectedTabDetails={selectedTabDetails}
          isChatLoading={isLoading}
        />

        {/* Show FilesList when selected */}
        {showFilesList && (
          <Box
            sx={{
              position: "fixed",
              top: 80,
              left: open ? 260 : 68,
              right: rightPanelOffset,
              bottom: 100,
              padding: 3,
              overflowY: "none",
              backgroundColor: "background.default",
              zIndex: 2,
              boxSizing: "border-box",
            }}
          >
            <FilesList
              onSelectFile={handleFileSelect}
              onDeleteFile={handleFileDelete}
              onKeyDown={(e) => {
                if (e.key === "Enter" && input.trim()) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
          </Box>
        )}

        {/* CHIP Repository Hub full-page view */}
        {showSharePointHub && (
          <Box
            sx={{
              position: "fixed",
              top: 80,
              left: open ? 260 : 68,
              right: rightPanelOffset,
              bottom: 0,
              backgroundColor: "background.default",
              zIndex: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SharePointPage />
          </Box>
        )}

        {/* Admin Dashboard full-page view */}
        {showAdmin && (
          <Box
            sx={{
              position: "fixed",
              top: 80,
              left: open ? 260 : 68,
              right: rightPanelOffset,
              bottom: 0,
              backgroundColor: "background.default",
              zIndex: 2,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <AdminDashboard />
          </Box>
        )}

        {/* Chat content container */}
        {selectedItem.id === 1 && !showFilesList && (
          <Box
            sx={{
              position: "fixed",
              top: 80,
              left: open ? 260 : 68,
              right: rightPanelOffset,
              bottom: 0,
              padding: "0",
              overflow: "hidden",
              transition: "all 0.3s ease",
              display: "flex",
              flexDirection: "column",
              zIndex: 1,
            }}
          >
            {/* Chat Content Area */}
            <ChatMessagesContainer
              activeTab={activeTab}
              chatContexts={chatContexts}
              messages={messages}
              scrollBoxRef={scrollBoxRef}
              handleStartNewChat={handleStartNewChat}
              handleCitationClickOpen={handleCitationClickOpen}
              handleCitationClose={handleCitationClose}
              citationopen={citationopen}
              setCitationOpen={setCitationOpen}
              tables={tables}
              handleDownloadAll={handleDownloadAll}
              citationclickvisible={citationclickvisible}
              handleRegenerateResponse={handleRegenerateResponse}
              onAbbreviationCandidateClick={handleAbbreviationCandidateClick}
              onSubmitFeedback={handleSubmitFeedback}
            />

            {/* Chat Input */}
            <ChatInput
              input={input}
              setInput={setInput}
              onSendMessage={handleSendMessage}
              onCancel={handleCancelRequest}
              isLoading={isLoading}
              open={open}
              isSettingsOpen={false}
              isDrawerExpanded={false}
            />
          </Box>
        )}

        {/* Dialogs and Modals */}
        {/* <PresetQuestionsDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          setInput={setInput}
        /> */}

        <ChatContextManager
          open={chatContextManagerOpen}
          onClose={() => setChatContextManagerOpen(false)}
          onSelectContext={handleSelectChatContext}
          currentContextId={currentChatContextId}
          onCreateContext={handleCreateChatContext}
          onUpdateContext={handleUpdateChatContext}
          onDeleteContext={handleDeleteChatContext}
        />

        <DocumentFiltersDrawer
          open={isDocumentFiltersDrawerOpen}
          onDrawerExpandedChange={setIsDocumentFiltersDrawerExpanded}
          setFilterDict={setFilterDict}
          isDisabled={isLoading}
          isAuthenticated={isAuthenticated}
        />
      </Grid>

      {/* Microsoft OAuth Loading Screen */}
      {isMicrosoftAuthLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              padding: 4,
              borderRadius: 2,
              textAlign: 'center',
              maxWidth: 400,
            }}
          >
            <Box sx={{ mb: 3 }}>
              <FullScreenLoader
                loading={true}
                message="Completing Microsoft Authentication..."
                size={50}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                mb: 1,
                fontFamily: "'Poppins', sans-serif",
                color: "text.primary",
              }}
            >
              Completing Microsoft Authentication...
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center' }}
            >
              Please wait while we complete your authentication.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Authentication Modal */}
      {!isAuthenticated && !isMicrosoftAuthLoading && (
        <PasswordModal
          onAuthenticated={(authStatus) => {
            setIsAuthenticated(authStatus);
            if (authStatus) {
              localStorage.setItem('authenticated', 'true');
            }
          }}
        />
      )}
    </>
  );
};

MainPage.propTypes = {
  initialTab: PropTypes.string,
  contentView: PropTypes.any
};

export default MainPage;